import { useState, useEffect, useCallback } from 'react';

const GITHUB_TOKEN_KEY = 'github_access_token';

export interface GitHubRepo {
  id: number;
  name: string;
  fullName: string;
  url: string;
  description: string | null;
  language: string | null;
  stars: number;
  updatedAt: string;
  isPrivate: boolean;
}

export function useGitHubAuth() {
  const [token, setToken] = useState<string | null>(null);
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 初期化時にトークンを確認
  useEffect(() => {
    // localStorageから読み込み
    const savedToken = localStorage.getItem(GITHUB_TOKEN_KEY);
    if (savedToken) {
      setToken(savedToken);
    }

    // URLフラグメントからトークンを取得（OAuthコールバック後）
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));

      const newToken = params.get('github_token');
      if (newToken) {
        setToken(newToken);
        localStorage.setItem(GITHUB_TOKEN_KEY, newToken);
        // URLからフラグメントを削除
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }

      const authError = params.get('github_error');
      if (authError) {
        setError(decodeURIComponent(authError));
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }
  }, []);

  // トークンが変わったらリポジトリを取得
  useEffect(() => {
    if (token) {
      fetchRepos();
    }
  }, [token]);

  // リポジトリ取得
  const fetchRepos = useCallback(async () => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/github/repos', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // トークンが無効な場合はクリア
          logout();
          throw new Error(data.error || 'GitHub認証が必要です');
        }
        throw new Error(data.error || 'リポジトリ取得に失敗しました');
      }

      setRepos(data.repos || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // 認証開始
  const login = useCallback(() => {
    window.location.href = '/api/github/auth';
  }, []);

  // ログアウト
  const logout = useCallback(() => {
    setToken(null);
    setRepos([]);
    localStorage.removeItem(GITHUB_TOKEN_KEY);
  }, []);

  return {
    isAuthenticated: !!token,
    repos,
    isLoading,
    error,
    login,
    logout,
    refetch: fetchRepos,
  };
}
